import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSearch, FiUser, FiShoppingBag, FiInstagram } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import client from '../shopify';

const HeaderContainer = styled.header`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 15px 40px;
  border-bottom: 1px solid #000;
  background-color: #fff;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 12px 16px; /* tighter on mobile for better spacing with title */
    padding-bottom: 16px; /* add a bit more space below to separate from border */
    justify-content: space-between; /* left icons and right icons on edges */
  }
`;

const Logo = styled.h1`
  font-size: 2.5em;
  font-weight: normal;
  letter-spacing: 1.5px;
  color: black;
  margin: 0;
  transition: color 0.3s ease-in-out;
  cursor: pointer;
  position: absolute;
  left: 50%;
  transform: translate(-50%, -4px);
  white-space: nowrap;

  &:hover {
    color: #543230;
  }

  /* On mobile, subtly nudge the logo upward to avoid touching the divider line */
  @media (max-width: 768px) {
    transform: translate(-50%, -2px);
  }
`;

const LeftIcons = styled.div`
  display: none;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    display: flex; /* show only on mobile */
  }

  a {
    display: inline-flex;
    text-decoration: none;
  }

  svg {
    font-size: 1.3em;
    cursor: pointer;
    color: #333;
    transition: color 0.3s ease-in-out;

    &:hover {
      color: #543230;
    }
  }
`;

const DesktopOnly = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 20px; /* match RightIcons gap for uniform spacing */
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const RightIcons = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  margin-left: auto; /* push to right when LeftIcons is hidden on desktop */
  
  a {
    display: inline-flex;
    text-decoration: none;
  }
  
  svg {
    font-size: 1.3em;
    cursor: pointer;
    color: #333;
    transition: color 0.3s ease-in-out;
    
    &:hover {
      color: #543230;
    }
  }
`;

const CartIconWrapper = styled.div`
  position: relative;
  display: inline-flex;
  cursor: pointer;
`;

const CartCount = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #e53935; /* Red color */
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 11px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
`;

const Header = ({ onCartToggle }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const INSTAGRAM_URL = process.env.REACT_APP_INSTAGRAM_URL || 'https://www.instagram.com/ilsprezzatura/';
  const [cartItemCount, setCartItemCount] = useState(0);
  
  const fetchCartCount = async () => {
    const cartId = localStorage.getItem('cartId');
    if (!cartId) {
      setCartItemCount(0);
      return;
    }
    
    try {
      const { data } = await client.request(
        `query getCart($cartId: ID!) {
          cart(id: $cartId) {
            totalQuantity
          }
        }`,
        { variables: { cartId } }
      );
      
      if (data.cart) {
        setCartItemCount(data.cart.totalQuantity || 0);
      }
    } catch (error) {
      console.error('Error fetching cart count:', error);
      localStorage.removeItem('cartId');
      setCartItemCount(0);
    }
  };
  
  useEffect(() => {
    fetchCartCount();
    
    const handleCartUpdate = () => {
      fetchCartCount();
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  const handleProfileClick = () => {
    if (currentUser) {
      navigate('/account');
    } else {
      navigate('/login');
    }
  };

  return (
    <HeaderContainer>
      <LeftIcons>
        <FiSearch />
        <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
          <FiInstagram />
        </a>
      </LeftIcons>
      <Logo className="script-font" onClick={() => navigate('/')}>Sprezzatura</Logo>

      <RightIcons>
        <DesktopOnly>
          <FiSearch />
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <FiInstagram />
          </a>
        </DesktopOnly>
        <FiUser onClick={handleProfileClick} />
        <CartIconWrapper onClick={onCartToggle}>
          <FiShoppingBag />
          {cartItemCount > 0 && <CartCount>{cartItemCount}</CartCount>}
        </CartIconWrapper>
      </RightIcons>
    </HeaderContainer>
  );
};

export default Header;
